import React, { useState, useEffect } from 'react';
import exchangeService from '../../services/exchangeService';
import ExchangeNav from './ExchangeNav';
import { FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';
import '../../MatchRequestList.css';

// 내가 받은 매칭 요청과 내가 보낸 매칭 요청을 탭으로 나눠서 보여주는 화면이다.
// 받은 요청에서는 수락/거절 버튼을 누를 수 있고, 보낸 요청은 상태(대기중)만 보여준다.
const MatchRequestList = () => {
  const [activeTab, setActiveTab] = useState('received'); // 'received' | 'sent'
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const [receivedRes, sentRes] = await Promise.all([
        exchangeService.getReceivedRequests(),
        exchangeService.getSentRequests(),
      ]);
      setReceivedRequests(receivedRes.data || []);
      setSentRequests(sentRes.data || []);
    } catch (err) {
      console.error('매칭 요청 목록 조회 오류:', err);
      setErrorMessage('매칭 요청 목록을 불러오는 중 오류가 발생했다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    try {
      setProcessingId(requestId);
      setErrorMessage('');
      await exchangeService.acceptMatchRequest(requestId);
      setSuccessMessage('매칭이 성사되었다! 채팅함에서 대화를 시작할 수 있다.');
      // 수락한 요청은 목록에서 지운다.
      setReceivedRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error('수락 처리 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      setErrorMessage(serverMessage || '수락 처리 중 오류가 발생했다.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm('이 매칭 요청을 거절하겠다?')) {
      return;
    }
    try {
      setProcessingId(requestId);
      setErrorMessage('');
      await exchangeService.rejectMatchRequest(requestId);
      setReceivedRequests((prev) => prev.filter((r) => r.id !== requestId));
    } catch (err) {
      console.error('거절 처리 오류:', err);
      const serverMessage = err.response && err.response.data && err.response.data.message;
      setErrorMessage(serverMessage || '거절 처리 중 오류가 발생했다.');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <>
        <ExchangeNav />
        <div className="exchange-loading">
          <FaSpinner className="exchange-spinner" />
          <p>불러오는 중이다...</p>
        </div>
      </>
    );
  }

  const list = activeTab === 'received' ? receivedRequests : sentRequests;

  return (
    <>
      <ExchangeNav />
      <div className="match-request-container">
        <h1 className="match-request-title">매칭 요청</h1>

        {errorMessage && <div className="exchange-error">{errorMessage}</div>}
        {successMessage && <div className="exchange-success">{successMessage}</div>}

        <div className="match-request-tabs">
          <button
            className={activeTab === 'received' ? 'active' : ''}
            onClick={() => setActiveTab('received')}
          >
            받은 요청 ({receivedRequests.length})
          </button>
          <button
            className={activeTab === 'sent' ? 'active' : ''}
            onClick={() => setActiveTab('sent')}
          >
            보낸 요청 ({sentRequests.length})
          </button>
        </div>

        {list.length === 0 ? (
          <div className="exchange-empty-state">
            <p>{activeTab === 'received' ? '받은 매칭 요청이 없다.' : '보낸 매칭 요청이 없다.'}</p>
          </div>
        ) : (
          <div className="match-request-list">
            {list.map((request) => (
              <div key={request.id} className="match-request-card">
                <div className="match-request-info">
                  <span className="match-request-nickname">
                    {activeTab === 'received' ? request.senderNickname : request.receiverNickname}
                  </span>
                  <span className="match-request-status">
                    {request.status === 'PENDING' ? '대기중' : request.status}
                  </span>
                </div>

                {activeTab === 'received' && (
                  <div className="match-request-actions">
                    <button
                      className="match-request-accept-button"
                      onClick={() => handleAccept(request.id)}
                      disabled={processingId === request.id}
                    >
                      <FaCheck /> 수락
                    </button>
                    <button
                      className="match-request-reject-button"
                      onClick={() => handleReject(request.id)}
                      disabled={processingId === request.id}
                    >
                      <FaTimes /> 거절
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default MatchRequestList;
